import { SocketConnector } from "@sdk/@libs/socket-connector";
import { isOrgOwner, summarizeUserAccess } from "@sdk/access/access-role-templates";
import { iUser } from "@sdk/user-management/user-management.models";

import { HandleSocketEvents } from "@socket-engine/handle-socket-events";

import debounce from "lodash/debounce";
import { Store } from "redux";
import watch from "redux-watch";
import * as Sentry from "@sentry/browser";
import { UserTracker } from "user-tracker";
import { justWait } from "utils/just-wait";
import { SDK } from "../@sdk/sdk";
import { updateRecentLoginData } from "./modules/app-state/app-state.actions";

import { persistorSelector } from "./modules/ui-state/ui-state.selectors";
import { logout } from "./modules/users/users.actions";
import { loadAllUsers, reloadCurrentUser } from "./modules/users/users.helpers";
import {
  selectAccessToken,
  selectCurrentUser,
  selectCurrentUserData,
} from "./modules/users/users.selectors";
import { loadOrganization } from "./modules/workspace/workspace.helpers";
import {
  selectOrganization,
  selectOrganizationLogo,
  selectWhitelistedIPs,
} from "./modules/workspace/workspace.selectors";
import { LoadAllCustomerPaymentMethods } from "./modules/customer-payment-methods/customer-payment-methods.helpers";
import { LoadAllSupplierPaymentMethods } from "./modules/supplier-payment-methods/supplier-payment-methods.helpers";
import { LoadAllBrands } from "./modules/brands/brands.helpers";
import { iStore } from "./store.model";
import { toast } from "sonner";

type SelectorFunction<T> = (state: iStore) => T;
type WatchCallback<T> = (newVal?: T, oldVal?: T, objectPath?: string) => void;

export const RegisterWatchers = (store: Store) => {
  function TypedWatch<T>(
    selector: SelectorFunction<T>
  ): (callback: WatchCallback<T>) => () => any {
    return watch(() => selector(store.getState())) as any;
  }

  /**
   *
   * Initiate
   *
   */

  const onStateRehydrate = debounce((newVal, oldVal, objectPath) => {
    // Write functions that needs to be firing when store initiated
  }, 200);

  const onIdentifyChange = debounce(
    (newVal?: any, oldVal?: any, objectPath?) => {
      // console.log("Identify Changed");
      const user = selectCurrentUser(store.getState());
      const organization = selectOrganization(store.getState());
      const accessToken = selectAccessToken(store.getState());

      if (organization && user) {
        store.dispatch(
          updateRecentLoginData({
            // Org Data
            domain: organization.domain,
            organizationLogo: organization.data.logo,
            // User Data
            email: user.credentials.email,
            firstName: user.data.firstName,
            lastName: user.data.lastName,
            avatar: user.data.avatar,
            // Access Data
            accessToken: accessToken,
          })
        );
      }
    },
    200
  );

  const onAccessTokenChangeInstant = (token: any) => {
    SDK.configure({
      token: token,
    });
  };

  const addWatcherStateListeners = () => {
    SocketConnector.socket.on("connect", () => {
      console.log("ReWatch Entities");
      reWatchEntities();
    });

    SocketConnector.socket.on("reconnect", () => {
      console.log("Watch Connect");
      reWatchEntities();
    });

    SocketConnector.socket.on("disconnect", () => {
      console.log("Watch Disconnect");
      if (SocketConnector.socket.id) {
        watchedEntities[SocketConnector.socket.id] = [];
      }
    });
  };

  const reWatchEntities = () => {
    // const list = selectAllLoadedConversations(store.getState());
    // const newEntitiesToWatch = _.without(list || []);
    // watchedEntities[SocketConnector.socket.id] = [...newEntitiesToWatch];
    // if (newEntitiesToWatch.length > 0) {
    //   const entityChunks = chunk(newEntitiesToWatch || [], 100);
    //   const process = async () => {
    //     for (const chunk of entityChunks) {
    //       SocketConnector.socket.emit("WATCH_ENTITIES", chunk || []);
    //       await justWait(400);
    //     }
    //   };
    //   process();
    // }
  };

  const onAccessTokenChange = debounce((token, oldVal, objectPath) => {
    // Write functions that needs to be firing when store initiated
    SDK.configure({
      token: token,
    });
    // On Logout, no need to load the account
    if (token) {
      loadOrganization(store).catch((e) => {
        console.log("Error while loading organization", e);
        // Failed. Ideally Logout
        toast.info("Loading Workspace Failed");
        store.dispatch(logout());
      });
      loadAllUsers()(store, true).catch((e) => {
        console.log("Error while loading all users");
        // Failed. Ideally Logout
        toast.info("Loading Users Failed");
        store.dispatch(logout());
      });
      if (!selectCurrentUser(store.getState())) {
        reloadCurrentUser(store).catch((e) => {
          console.log("Error while loading user");
          // Failed. Ideally Logout
          toast.info("Loading User Failed");
          store.dispatch(logout());
        });
      }
      onIdentifyChange();
    }

    // Connect and Disconnect Socket Server // Probably have to move this to main component
    if (token) {
      SocketConnector.connect(token);
      HandleSocketEvents(SocketConnector.socket, store);
      // Init Away Detector
      // AwayDetectorService.init(store);
      // Init User Presence Logger
      // Todo: Uncomment
      // UserPresenceLoggerService.init(store);
      addWatcherStateListeners();
    } else {
      SocketConnector.disconnect();
    }
  }, 200);

  const onCurrentUserChange = debounce(
    (newVal?: iUser, oldVal?: iUser, objectPath?) => {
      if (newVal && newVal.id) {
        (window as any)["CC_CURRENT_USER_ID"] = newVal.id;
      }
      // Auto Logout Deactivated Users
      if (newVal && newVal.metaData.isActive === false) {
        toast.info("User is not active");
        store.dispatch(logout());
      }
      if (newVal?.credentials?.email) {
        Sentry.setUser({
          email: newVal?.credentials.email,
        });
        Sentry.setContext("data", {
          name: `${newVal.data.firstName}`,
          organizationId: newVal.organizationId,
          email: newVal?.credentials.email,
        });
        const organization = selectOrganization(store.getState());
        // Mixpanel User Identification
        UserTracker.identify(newVal.id);
        const mixPanelProfile = {
          $name: [newVal.data.firstName, newVal.data.lastName]
            .filter((e) => e)
            .join(" "),
          $first_name: newVal.data.firstName,
          $last_name: newVal.data.lastName,
          $email: newVal?.credentials.email,
          "User Role": summarizeUserAccess(newVal),
          organizationId: newVal.organizationId,
          workspace: organization?.domain,
        };
        UserTracker.setUserProperties(mixPanelProfile);
      }
    },
    200
  );

  const watchedEntities: { [socketId: string]: string[] } = {};

  const onOrganizationChange = debounce(
    (organization: ReturnType<typeof selectOrganization>) => {
      if (organization?.id) {
        LoadAllBrands(store as Store<iStore>);
        LoadAllCustomerPaymentMethods(store as Store<iStore>);
        LoadAllSupplierPaymentMethods(store as Store<iStore>);
      }
    },
    300
  );

  const onAllowedIPChange = debounce((allowedIps, oldVal, objectPath) => {
    if (allowedIps && allowedIps.length > 0) {
      let user = selectCurrentUser(store.getState());
      // Todo: Could Improve this
      (async () => {
        if (!user) {
          await justWait(2000);
        }
        user = selectCurrentUser(store.getState());
        if (!user) {
          return;
        }
        if (!isOrgOwner(user)) {
          SDK.validateAccess()
            .then((data) => {
              if (!data.valid) {
                toast.warning("Your IP address is blocked");
                store.dispatch(logout());
              }
            })
            .catch((e) => {
              // Ignore
            });
        }
      })();
    }
  }, 200);

  /**
   *
   * Watchers
   *
   */

  const persistStateWatcher = TypedWatch(persistorSelector)(onStateRehydrate);

  const accessTokenWatcher = TypedWatch(selectAccessToken)(onAccessTokenChange);

  const accessTokenWatcher2 = TypedWatch(selectAccessToken)(
    onAccessTokenChangeInstant
  );

  const CurrentUserWatcher = TypedWatch(selectCurrentUser)(onCurrentUserChange);

  const AllowedIPChange = TypedWatch(selectWhitelistedIPs)(onAllowedIPChange);

  const userOrganizationLogoChangeWatcher = TypedWatch(selectOrganizationLogo)(
    onIdentifyChange
  );

  const userDataWatcher = TypedWatch(selectCurrentUserData)(onIdentifyChange);

  const organizationWatcher = TypedWatch(selectOrganization)(onOrganizationChange);

  store.subscribe(() => {
    // Add Watchers Here
    persistStateWatcher();
    accessTokenWatcher();
    accessTokenWatcher2();
    CurrentUserWatcher();
    AllowedIPChange();
    userOrganizationLogoChangeWatcher();
    userDataWatcher();
    organizationWatcher();
  });
};
